/**
 * OpenMeta — Theme system (light/dark public).
 * - Persistance : localStorage `om_theme`
 * - Défaut : prefers-color-scheme → sinon 'light'
 * - Applique data-theme sur <html>
 * - useTheme() → { theme, setTheme, toggle }
 * - forceLightScope() : hook pour ancrer une route en light (utile /admin)
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'om_theme';
const SUPPORTED = ['light', 'dark'];

export function detectInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && SUPPORTED.includes(stored)) return stored;
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch (_) {}
  return 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggle: () => {},
  setForceLight: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Le vrai init a été fait par le script anti-flash dans index.html
    if (typeof document !== 'undefined') {
      const attr = document.documentElement.getAttribute('data-theme');
      if (attr === 'dark' || attr === 'light') return attr;
    }
    return detectInitialTheme();
  });
  // Compteur de "verrouillages light" pour supporter plusieurs consommateurs concurrents.
  const [forceLightCount, setForceLightCount] = useState(0);

  const setTheme = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    setThemeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  const toggle = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const setForceLight = useCallback((next) => {
    // next = true → incrémente le compteur ; next = false → décrémente
    setForceLightCount((c) => Math.max(0, c + (next ? 1 : -1)));
  }, []);

  // Un unique useEffect applique le data-theme : soit forcé light, soit état utilisateur.
  useEffect(() => {
    applyTheme(forceLightCount > 0 ? 'light' : theme);
  }, [theme, forceLightCount]);

  const value = useMemo(
    () => ({ theme, setTheme, toggle, setForceLight }),
    [theme, setTheme, toggle, setForceLight],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

/**
 * Force le thème light pendant la durée de vie du composant (ex: routes /admin).
 * S'appuie sur un compteur dans le contexte pour rester cohérent avec le useEffect
 * d'application du thème (évite la race condition child-first useEffect).
 */
export function useForceLightTheme() {
  const { setForceLight } = useContext(ThemeContext);
  useEffect(() => {
    setForceLight(true);
    return () => setForceLight(false);
  }, [setForceLight]);
}

export const THEME_STORAGE_KEY = STORAGE_KEY;
